import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Inbox, MapPin, Tag, Phone, ExternalLink, Loader2, UserCheck,
    RefreshCw, Mail, Building2, Plus, X, Clock, Zap,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const STATUS_LABELS = {
    new: "Neu", assigned: "Zugewiesen", in_progress: "In Bearbeitung",
    won: "Gewonnen", lost: "Verloren", archived: "Archiviert",
};
const STATUS_STYLE = {
    new: "bg-amber-100 text-amber-900 border-amber-300",
    assigned: "bg-blue-100 text-blue-900 border-blue-300",
    in_progress: "bg-indigo-100 text-indigo-900 border-indigo-300",
    won: "bg-emerald-100 text-emerald-900 border-emerald-300",
    lost: "bg-gray-100 text-gray-700 border-gray-300",
    archived: "bg-gray-100 text-gray-500 border-gray-300",
};
const CONDITION_BADGE = {
    Neufahrzeug: { label: "Neu", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", pool: true },
    Tageszulassung: { label: "TZ", cls: "bg-teal-100 text-teal-900 border-teal-300", pool: true },
    Vorführfahrzeug: { label: "Vorführer", cls: "bg-amber-100 text-amber-900 border-amber-300", pool: false },
    Gebrauchtfahrzeug: { label: "Gebraucht", cls: "bg-gray-100 text-gray-700 border-gray-300", pool: false },
};
function ConditionBadge({ condition, testid }) {
    const c = CONDITION_BADGE[condition];
    if (!c) return null;
    return (
        <span
            className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border ${c.cls}`}
            title={c.pool ? "Fällt in den gemeinsamen Bildpool (NW/TZ) — Bilder werden beim Umwandeln automatisch angehängt" : "Symbolfoto-Vorschläge beim Umwandeln"}
            data-testid={testid}
        >
            {c.label}{c.pool ? " · Bildpool" : ""}
        </span>
    );
}
const fmtEur = (v) => (typeof v === "number"
    ? new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v)
    : "—");

export default function AdminLeadsPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ items: [], counts: {} });
    const [busy, setBusy] = useState(true);
    const [status, setStatus] = useState("");
    const [region, setRegion] = useState("");
    const [q, setQ] = useState("");
    const [assignLead, setAssignLead] = useState(null);
    const [autoHours, setAutoHours] = useState(null);
    const [savingHours, setSavingHours] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const params = {};
            if (status) params.status = status;
            if (region) params.region = region;
            if (q) params.q = q;
            const r = await api.get("/admin/leads", { params });
            setData(r.data);
        } catch (e) {
            console.error("leads load failed", e);
        } finally { setBusy(false); }
    }, [status, region, q]);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") { navigate("/"); return; }
        load();
        api.get("/admin/lead-settings").then((r) => setAutoHours(r.data.auto_assign_hours)).catch(() => {});
    }, [user, loading, navigate, load]);

    const saveHours = async () => {
        setSavingHours(true);
        try {
            const v = Math.max(0, parseInt(autoHours, 10) || 0);
            await api.put("/admin/lead-settings", { auto_assign_hours: v });
            setAutoHours(v);
            toast.success(v === 0 ? "Auto-Zuweisung deaktiviert" : `Frist: ${v} h`);
        } catch { toast.error("Speichern fehlgeschlagen"); }
        finally { setSavingHours(false); }
    };

    const changeStatus = async (lead, newStatus) => {
        await api.patch(`/admin/leads/${lead.id}`, { status: newStatus });
        load();
    };

    const confirmSuggested = async (lead) => {
        await api.post(`/admin/leads/${lead.id}/assign`, { dealer_id: lead.suggested_dealer_id });
        load();
    };

    if (loading || !user) return <div className="p-12">Lädt…</div>;

    const counts = data.counts || {};
    const kpis = [
        { key: "total", label: "Gesamt", color: "text-[#111827]" },
        { key: "new", label: "Neu", color: "text-amber-700" },
        { key: "assigned", label: "Zugewiesen", color: "text-blue-700" },
        { key: "won", label: "Gewonnen", color: "text-emerald-700" },
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-leads-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-[#111827] flex items-center gap-2">
                        <Inbox className="w-7 h-7 text-[#FFCC00]" /> Eingehende Leads
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Angebote von Autobörsen (per E-Mail an <span className="font-mono">angebote@twowheels.info</span>) — regional zuweisen.
                    </p>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    <div className="flex items-center gap-1.5 border border-gray-300 rounded-sm px-2.5 py-1.5" data-testid="admin-leads-auto-setting" title="Nicht bestätigte Leads werden nach Ablauf automatisch dem regionalen Top-Match zugewiesen (0 = aus).">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-600 font-bold">Auto-Zuweisung nach</span>
                        <input type="number" min="0" max="720" value={autoHours ?? ""} onChange={(e) => setAutoHours(e.target.value)} className="w-14 border border-gray-300 rounded-sm px-1.5 py-0.5 text-sm font-mono-num" data-testid="admin-leads-auto-hours" />
                        <span className="text-xs text-gray-600">h</span>
                        <button onClick={saveHours} disabled={savingHours} className="text-xs font-bold text-indigo-600 hover:text-indigo-800 disabled:opacity-50" data-testid="admin-leads-auto-save">Speichern</button>
                    </div>
                    <button onClick={load} className="inline-flex items-center gap-2 border border-gray-300 rounded-sm px-3 py-2 text-sm font-bold hover:bg-gray-50" data-testid="admin-leads-refresh">
                        <RefreshCw className="w-4 h-4" /> Aktualisieren
                    </button>
                    <button onClick={() => setAssignLead({ manual: true })} className="inline-flex items-center gap-2 bg-[#111827] text-white rounded-sm px-3 py-2 text-sm font-bold hover:bg-black" data-testid="admin-leads-add">
                        <Plus className="w-4 h-4" /> Lead manuell
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {kpis.map((k) => (
                    <div key={k.key} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`admin-leads-kpi-${k.key}`}>
                        <div className={`text-3xl font-black font-mono-num ${k.color}`}>{counts[k.key] ?? 0}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mt-1">{k.label}</div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-2 mb-4 items-center">
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-gray-300 rounded-sm px-3 py-2 text-sm" data-testid="admin-leads-filter-status">
                    <option value="">Alle Status</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region (PLZ/Stadt)" className="border border-gray-300 rounded-sm px-3 py-2 text-sm" data-testid="admin-leads-filter-region" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche (Marke/Modell/Börse)" className="border border-gray-300 rounded-sm px-3 py-2 text-sm flex-1 min-w-[200px]" data-testid="admin-leads-filter-q" />
            </div>

            {busy ? (
                <div className="flex items-center gap-2 text-gray-500 py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Lädt…</div>
            ) : data.items.length === 0 ? (
                <div className="text-center text-gray-500 py-16 border border-dashed border-gray-300 rounded-sm" data-testid="admin-leads-empty">
                    Keine Leads gefunden.
                </div>
            ) : (
                <div className="space-y-3" data-testid="admin-leads-list">
                    {data.items.map((lead) => {
                        const v = lead.vehicle || {};
                        const loc = lead.location || {};
                        return (
                            <div key={lead.id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`admin-lead-${lead.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border ${STATUS_STYLE[lead.status]}`} data-testid={`admin-lead-status-${lead.id}`}>{STATUS_LABELS[lead.status]}</span>
                                            {lead.boerse && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm bg-[#FFCC00]/20 text-[#7a5b00] border border-[#FFCC00]">{lead.boerse}</span>}
                                            <ConditionBadge condition={v.condition} testid={`admin-lead-condition-${lead.id}`} />
                                            {lead.extraction_method === "ai" && <span className="text-[10px] text-gray-400">KI-erkannt</span>}
                                        </div>
                                        <div className="text-lg font-black text-[#111827] mt-1">
                                            {[v.brand, v.model].filter(Boolean).join(" ") || lead.subject}
                                        </div>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                                            <span className="inline-flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> {fmtEur(v.price)}</span>
                                            {v.year && <span>EZ {v.year}</span>}
                                            {typeof v.mileage === "number" && <span>{v.mileage.toLocaleString("de-DE")} km</span>}
                                            <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {[loc.zip, loc.city].filter(Boolean).join(" ") || "—"}</span>
                                            {lead.seller?.phone && <a href={`tel:${lead.seller.phone}`} className="inline-flex items-center gap-1 text-blue-700"><Phone className="w-3.5 h-3.5" /> {lead.seller.phone}</a>}
                                            <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {lead.from_email}</span>
                                            {lead.url && <a href={lead.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700"><ExternalLink className="w-3.5 h-3.5" /> Inserat</a>}
                                        </div>
                                        {lead.assigned_dealer_label && (
                                            <div className="text-xs text-emerald-700 font-bold mt-2 inline-flex items-center gap-1" data-testid={`admin-lead-assigned-${lead.id}`}>
                                                <Building2 className="w-3.5 h-3.5" /> Zugewiesen: {lead.assigned_dealer_label}
                                                {lead.auto_assigned && (
                                                    <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-indigo-100 text-indigo-800 border border-indigo-200" data-testid={`admin-lead-auto-${lead.id}`}>
                                                        <Zap className="w-3 h-3" /> Auto
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {!lead.assigned_dealer_id && lead.suggested_dealer_id && (
                                            <div className="mt-2 flex items-center gap-2 flex-wrap bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-1.5" data-testid={`admin-lead-suggested-${lead.id}`}>
                                                <span className="text-xs text-emerald-800">
                                                    Vorschlag (Region): <strong>{lead.suggested_dealer_label}</strong>
                                                </span>
                                                <button onClick={() => confirmSuggested(lead)} className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded-sm px-2.5 py-1 text-xs font-bold hover:bg-emerald-700" data-testid={`admin-lead-confirm-suggested-${lead.id}`}>
                                                    <UserCheck className="w-3.5 h-3.5" /> Bestätigen
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-2 items-end">
                                        <button onClick={() => setAssignLead(lead)} className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] rounded-sm px-3 py-2 text-sm font-bold hover:brightness-95" data-testid={`admin-lead-assign-btn-${lead.id}`}>
                                            <UserCheck className="w-4 h-4" /> {lead.assigned_dealer_id ? "Neu zuweisen" : "Zuweisen"}
                                        </button>
                                        <select value={lead.status} onChange={(e) => changeStatus(lead, e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-xs" data-testid={`admin-lead-status-select-${lead.id}`}>
                                            {Object.entries(STATUS_LABELS).map(([k, val]) => <option key={k} value={k}>{val}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {assignLead && !assignLead.manual && (
                <AssignDialog lead={assignLead} onClose={() => setAssignLead(null)} onDone={() => { setAssignLead(null); load(); }} />
            )}
            {assignLead?.manual && (
                <ManualLeadDialog onClose={() => setAssignLead(null)} onDone={() => { setAssignLead(null); load(); }} />
            )}
        </div>
    );
}

function AssignDialog({ lead, onClose, onDone }) {
    const [dealers, setDealers] = useState(null);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/admin/leads/${lead.id}/suggest-dealers`);
                setDealers(r.data.dealers || []);
            } catch { setDealers([]); }
        })();
    }, [lead.id]);

    const assign = async (dealerId) => {
        setSaving(true);
        try {
            await api.post(`/admin/leads/${lead.id}/assign`, { dealer_id: dealerId, note });
            onDone();
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="admin-lead-assign-dialog">
            <div className="bg-white rounded-sm max-w-lg w-full max-h-[85vh] overflow-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h2 className="font-black text-lg">Lead regional zuweisen</h2>
                    <button onClick={onClose} data-testid="admin-lead-assign-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Notiz an den Händler (optional)" className="w-full border border-gray-300 rounded-sm p-2 text-sm" data-testid="admin-lead-assign-note" />
                    {dealers === null ? (
                        <div className="flex items-center gap-2 text-gray-500 py-6 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Händler werden geladen…</div>
                    ) : (
                        <div className="space-y-2">
                            {dealers.map((d) => (
                                <button key={d.id} disabled={saving} onClick={() => assign(d.id)} className="w-full flex items-center justify-between border border-gray-200 rounded-sm px-3 py-2.5 hover:border-[#FFCC00] hover:bg-[#FFCC00]/5 text-left disabled:opacity-50" data-testid={`admin-lead-assign-dealer-${d.id}`}>
                                    <div>
                                        <div className="font-bold text-sm text-[#111827]">{d.label}</div>
                                        <div className="text-xs text-gray-500">{[d.postal_code, d.city].filter(Boolean).join(" ") || "—"}{d.site_code ? ` · ${d.site_code}` : ""}</div>
                                    </div>
                                    {d.match_score > 0 && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300">Region-Match</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ManualLeadDialog({ onClose, onDone }) {
    const [f, setF] = useState({ brand: "", model: "", price: "", zip: "", city: "", seller_name: "", seller_phone: "", boerse: "", url: "" });
    const [saving, setSaving] = useState(false);
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/admin/leads", {
                ...f, price: f.price ? Number(f.price) : null,
            });
            onDone();
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="admin-lead-manual-dialog">
            <div className="bg-white rounded-sm max-w-lg w-full max-h-[85vh] overflow-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h2 className="font-black text-lg">Lead manuell anlegen</h2>
                    <button onClick={onClose}><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3">
                    <input placeholder="Marke" value={f.brand} onChange={set("brand")} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="manual-lead-brand" />
                    <input placeholder="Modell" value={f.model} onChange={set("model")} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="manual-lead-model" />
                    <input placeholder="Preis €" value={f.price} onChange={set("price")} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="manual-lead-price" />
                    <input placeholder="Börse" value={f.boerse} onChange={set("boerse")} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="manual-lead-boerse" />
                    <input placeholder="PLZ" value={f.zip} onChange={set("zip")} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="manual-lead-zip" />
                    <input placeholder="Stadt" value={f.city} onChange={set("city")} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="manual-lead-city" />
                    <input placeholder="Verkäufer-Name" value={f.seller_name} onChange={set("seller_name")} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="manual-lead-seller" />
                    <input placeholder="Telefon" value={f.seller_phone} onChange={set("seller_phone")} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="manual-lead-phone" />
                    <input placeholder="Inserat-URL" value={f.url} onChange={set("url")} className="border border-gray-300 rounded-sm p-2 text-sm col-span-2" data-testid="manual-lead-url" />
                </div>
                <div className="px-5 pb-5">
                    <button onClick={save} disabled={saving} className="w-full bg-[#111827] text-white rounded-sm py-2.5 font-bold hover:bg-black disabled:opacity-60" data-testid="manual-lead-save">
                        {saving ? "Speichert…" : "Lead anlegen"}
                    </button>
                </div>
            </div>
        </div>
    );
}
